import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { getLanguageFromLocale, type Locale } from '@seek/melways-sites';

import {
  getCountryCode,
  getLocale,
  getZone,
} from 'src/core/config/globalStates';
import {
  GetProfileSeekMaxDocument,
  type GetProfileSeekMaxQuery,
  type GetProfileSeekMaxQueryVariables,
  GetUserQueryDocument,
  type GetUserQueryQueryVariables,
  SeekMaxProfileDocument,
  type SeekMaxProfileQuery,
} from 'src/generated/gql/graphql';
import {
  generateLearningProfileMapper,
  generateSeekMaxProfileMapper,
  generateUserProfileMapper,
  type LearningProfile,
  type SeekMaxProfile,
} from 'src/mapper/userMapper';
import { client as csrClient } from 'src/modules/apollo/apolloClient';
import logger from 'src/modules/logger/logger';
import type { ConfigStateContext } from 'src/types';
import type { AppUserProfile } from 'src/types/userProfile';

import { ensureLearningProfile } from './learningProfileService';

// There's a problem where we fetch both token and userProfile at the same stage, however fetching userProfile does require the token as a header.
// Adding this additional header solves this problem.
export const getViewer = async (
  token: string,
  client: ApolloClient<NormalizedCacheObject> = csrClient,
): Promise<AppUserProfile> => {
  try {
    const variables: GetUserQueryQueryVariables = {};
    const additionalHeaders: Record<string, string> = {};
    additionalHeaders.authorization = `Bearer ${token}`;

    logger.info('getViewer', variables);

    const { data } = await client.query({
      query: GetUserQueryDocument,
      variables,
      context: {
        headers: {
          ...additionalHeaders,
        },
      },
    });

    return generateUserProfileMapper(data);
  } catch (error) {
    logger.error({ error }, 'getViewer');
    throw error;
  }
};

export const getSeekMaxProfile = async (
  config: Pick<ConfigStateContext, 'countryCode' | 'zone' | 'language'>,
  client: ApolloClient<NormalizedCacheObject> = csrClient,
): Promise<SeekMaxProfile> => {
  try {
    const { countryCode, zone, language } = config;

    await ensureLearningProfile();
    const variables: GetProfileSeekMaxQueryVariables = {
      countryCode: countryCode.toUpperCase() || getCountryCode(),
      zone: zone || getZone(),
      languageCode: language || getLanguageFromLocale(getLocale() as Locale),
    };

    logger.info('getSeekMaxProfile', variables);

    const { data } = await client.query<
      GetProfileSeekMaxQuery,
      GetProfileSeekMaxQueryVariables
    >({
      query: GetProfileSeekMaxDocument,
      variables,
    });

    return generateSeekMaxProfileMapper(data);
  } catch (error) {
    logger.error({ error }, 'getSeekMaxProfile');
    throw error;
  }
};

export const getSeekMaxLearningProfile = async (
  client: ApolloClient<NormalizedCacheObject> = csrClient,
): Promise<LearningProfile> => {
  try {
    await ensureLearningProfile();
    logger.info('getSeekMaxLearningProfile');

    const { data } = await client.query<SeekMaxProfileQuery>({
      query: SeekMaxProfileDocument,
    });

    return generateLearningProfileMapper(data);
  } catch (error) {
    logger.error({ error }, 'getSeekMaxLearningProfile');
    throw error;
  }
};
